<template>
  <div class="min-h-screen bg-gradient-to-br from-navy-50 via-white to-gold-50">
    <!-- Hero section -->
    <div class="relative overflow-hidden">
      <!-- Background pattern -->
      <div class="absolute inset-0 bg-gradient-to-r from-primary-600 via-primary-700 to-navy-800"></div>
      <div class="absolute inset-0 bg-black/10"></div>
      
      <!-- Decorative elements -->
      <div class="absolute top-0 left-0 w-full h-full">
        <div class="absolute top-20 left-10 w-32 h-32 bg-gold-400/20 rounded-full blur-3xl"></div>
        <div class="absolute bottom-20 right-10 w-40 h-40 bg-accent-400/20 rounded-full blur-3xl"></div>
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-white/10 rounded-full blur-3xl"></div>
      </div>
      
      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div class="text-center animate-fade-in">
          <h1 class="hero-title text-white mb-6">
            Notre <span class="bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text text-transparent">Comité & Directeur musical</span>
          </h1>
          <!--
          <p class="hero-subtitle text-white/90 max-w-3xl mx-auto">
          </p>
          -->
        </div>
      </div>
    </div>
    
    <!-- Committee content -->
    <div class="py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Loading state -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-20">
          <div class="relative">
            <div class="animate-spin rounded-full h-16 w-16 border-4 border-gold-200"></div>
            <div class="animate-spin rounded-full h-16 w-16 border-4 border-gold-600 border-t-transparent absolute top-0 left-0"></div>
          </div>
          <p class="mt-6 text-navy-600 text-lg font-medium">Chargement du comité...</p>
        </div>
        
        <!-- Error state -->
        <div v-else-if="error" class="text-center py-20">
          <div class="card-gradient max-w-md mx-auto">
            <div class="p-8">
              <div class="w-16 h-16 bg-accent-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg class="w-8 h-8 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <h3 class="text-xl font-bold text-navy-800 mb-4">Oups !</h3>
              <p class="text-navy-600 mb-6">Impossible de charger les informations du comité. Veuillez réessayer plus tard.</p>
              <button @click="loadCommittee" class="btn-primary">Réessayer</button>
            </div>
          </div>
        </div>
        
        <!-- Committee content -->
        <div v-else-if="committeeMembers.length > 0">
          <!-- Committee Members Section -->
          <div v-if="regularMembers.length > 0">
            <div class="text-center mb-12">
              <h2 class="text-3xl md:text-4xl font-bold text-navy-800 mb-4">
                <span class="bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text text-transparent">
                  Membres du Comité
                </span>
              </h2>
              <div class="w-24 h-1 bg-gradient-to-r from-yellow-400 to-yellow-600 mx-auto rounded-full"></div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div 
                v-for="member in regularMembers" 
                :key="member.id"
                class="group text-center hover-lift"
              >
                <div class="card-gradient group-hover:shadow-2xl transition-all duration-500 group-hover:scale-105">
                  <div class="p-8">
                    <!-- Member photo (only if available) -->
                    <div v-if="canDisplayImage(member.image_url)" class="mb-8 relative">
                      <div class="w-40 h-40 mx-auto rounded-3xl overflow-hidden shadow-2xl group-hover:shadow-3xl transition-all duration-500">
                        <img 
                          :src="member.image_url" 
                          :alt="member.name"
                          class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          @error="handleImageError"
                        />
                        <!-- Shine effect -->
                        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                      </div>
                    </div>
                    <!-- Member info -->
                    <h3 class="text-2xl font-bold text-yellow-700 mb-3 group-hover:text-yellow-600 transition-colors duration-300">
                      {{ member.name }}
                    </h3>
                    <p class="text-yellow-600 font-bold text-lg mb-6 bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text text-transparent">
                      {{ member.role }}
                    </p>
                    
                    <p v-if="member.description" class="text-navy-600 leading-relaxed text-base">
                      {{ member.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Musical Director Section -->
          <div v-if="musicalDirector" class="mt-20">
            <div class="text-center mb-12">
              <h2 class="text-3xl md:text-4xl font-bold text-navy-800 mb-4">
                <span class="bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
                  Directeur Musical
                </span>
              </h2>
              <div class="w-24 h-1 bg-gradient-to-r from-yellow-300 to-yellow-500 mx-auto rounded-full"></div>
            </div>
            
            <div class="max-w-2xl mx-auto">
              <div class="group text-center hover-lift">
                <div class="card-gradient group-hover:shadow-2xl transition-all duration-500 group-hover:scale-105">
                  <div class="p-8">
                    <!-- Director photo (only if available) -->
                    <div v-if="canDisplayImage(musicalDirector.image_url)" class="mb-8 relative">
                      <div class="w-48 h-48 mx-auto rounded-3xl overflow-hidden shadow-2xl group-hover:shadow-3xl transition-all duration-500">
                        <img 
                          :src="musicalDirector.image_url" 
                          :alt="musicalDirector.name"
                          class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          @error="handleImageError"
                        />
                        <!-- Shine effect -->
                        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                      </div>
                    </div>
                    <!-- Director info -->
                    <h3 class="text-3xl font-bold text-yellow-700 mb-4 group-hover:text-yellow-600 transition-colors duration-300">
                      {{ musicalDirector.name }}
                    </h3>
                    <p class="text-yellow-600 font-bold text-xl mb-6 bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text text-transparent">
                      {{ musicalDirector.role }}
                    </p>
                    
                    <p v-if="musicalDirector.description" class="text-navy-600 leading-relaxed text-lg">
                      {{ musicalDirector.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Empty state -->
        <div v-else class="text-center text-gray-500">
          <p>Aucune information sur le comité disponible pour le moment.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { committeeService } from '../services/committeeService'
import { canDisplayImage } from '../utils/imageUtils'

export default {
  name: 'Committee',
  data() {
    return {
      committeeMembers: [],
      loading: true,
      error: false
    }
  },
  computed: {
    musicalDirector() {
      return this.committeeMembers.find(member => 
        member.role.toLowerCase().includes('directeur musical') || 
        member.role.toLowerCase().includes('director musical')
      )
    },
    regularMembers() {
      return this.committeeMembers.filter(member => 
        !member.role.toLowerCase().includes('directeur musical') && 
        !member.role.toLowerCase().includes('director musical')
      )
    }
  },
  async mounted() {
    await this.loadCommittee()
  },
  methods: {
    async loadCommittee() {
      try {
        this.loading = true
        this.error = false
        this.committeeMembers = await committeeService.getCommitteeMembers()
      } catch (error) {
        console.error('Error loading committee:', error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    
    canDisplayImage(url) {
      return canDisplayImage(url)
    },
    
    handleImageError(event) {
      // Si l'image ne se charge pas, on peut ajouter une classe pour la masquer
      event.target.style.display = 'none'
    }
  }
}
</script>
